/* eslint-disable complexity */
import { TaskType } from '@scale/shared-types';
import { IWarningParams } from '@scale/shared/types/warning';
import { Claim } from '@scale/shared/types/queue';
import ReportTask, { ReportTaskRef } from '../reportTask/ReportTask';
import SkipTask from '../SkipTask';
import { Button, Tag, TaskHeader, Tooltip, Typography, RadioGroup, Switch } from '@scale/scaleui';
import { InfoAutosave } from './InfoAutosave';
// TODO: Remove this import once new task header fully rolled out
import { OldInfoAutosave } from './OldInfoAutosave';
import { IconTriggerAutosave } from './IconTriggerAutosave';
import { IconInlineFeedback } from './IconInlineFeedback';
import { SendHandler, SendTaskModal, SendTypes, SendStatus, TEXTS } from '../common/SendTask';
import { useEffect, useRef, useState } from 'react';
import { useWindowSize } from 'react-use-v13';
import { AutosaveHook } from 'react-scale/hooks/useAutosave';
import { IProjectJson } from '@scale/shared/types/project';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { QueryClientProvider as QueryClientProviderV4 } from '@tanstack/react-query-v4';
import { useTaskQueryClientV4 } from 'react-scale/hooks/useTaskQueryClient';
import {
  IndexedInteractionStepResponse,
  MultiTurnInteractionConfiguration,
} from '@scale/shared/types/chat';
import { AssignmentJson } from '@scale/shared/assignmentJson/types/AssignmentJson';
import { InfoDeadline } from './InfoDeadline';
import { ChipQualityPay } from './ChipQualityPay';
import { ChipPayTier } from './ChipPayTier';
import { InfoTimeSpent } from './InfoTimeSpent';
import InfoTurns from './InfoTurns';
import { ChatShareModal } from '../ThreadedInteraction/share/ChatShareModal';
import { useChatShare } from '../ThreadedInteraction/hooks/useChatShare';
import useSensitiveContentReporting from 'react-scale/hooks/useSensitiveContentReporting';
import { useChatLayoutStore } from '../ThreadedInteraction/hooks/useChatLayoutStore';
import { twMerge } from 'tailwind-merge';
import {
  useChatStoreExtract,
  useNumberOfTurns,
  useChatConfig,
  useIsRealCustomerAudit,
} from '../Chat/hooks/ChatStore.hooks';
import TemplateFeedback from 'react-scale/components/scalers/tasks/TemplateFeedback';
import {
  FeedbackConditions,
  ITaskFeedbackEntry,
  ITaskFeedbackEntryResponse,
  TaskFeedbackInput,
  hasFeedbackTemplateForType,
  filterEntriesByReviewLevel,
} from '@scale/shared/taskFeedback';
import { usePauseCapability } from 'react-scale/components/scalers/tasks/common/PauseCapabilityHook';
import PauseButton from 'react-scale/components/scalers/tasks/common/PauseButton';
import { Event, logEvent } from 'react-scale/lib/events';
import {
  ChatTheme,
  useChatThemeStore,
} from 'react-scale/components/scalers/tasks/ThreadedInteraction/hooks/useChatThemeStore';
import { useShowContributorInstructionsStore } from 'react-scale/components/scalers/tasks/ThreadedInteraction/hooks/useShowContributorInstructionsStore';
import _ from 'lodash';
import { useProjectOutlineStore } from 'react-scale/remotasks.com/components/Expert/ProjectOnboarding/stores/projectOutlineStore';
import { ProjectOnboardingOutline } from 'react-scale/remotasks.com/components/Expert/ProjectOnboarding/components/ProjectOnboardingOutline';
import useUnleashFeatureFlag from 'react-scale/hooks/useUnleashFeatureFlag';
import { analytics } from 'react-scale/lib/analytics/segment-source-scalers';
import { getPageLoadSingleton } from '@scale/frontend-lib/globalSingletons';

import OutlierLogo from './OutlierLogo';
import PauseModal from './PauseModal';
import { useClaimTime } from 'react-scale/hooks/useClaimTime';
import { useTimerData } from '../../withV2Timer';
import PaidTimeExceededModal from 'react-scale/components/scalers/tasks/OutlierTaskHeader/PaidTimeExceededModal';
import TaskHoursExceededModal from 'react-scale/components/scalers/tasks/OutlierTaskHeader/TaskHoursExceededModal';
import { getMaxPayableTime } from './utils';
import { usePayRate } from 'react-scale/hooks/usePayRate';
import { InfoIdleTime } from 'react-scale/components/scalers/tasks/OutlierTaskHeader/InfoIdleTime';
import { InfoTaskId } from 'react-scale/components/scalers/tasks/OutlierTaskHeader/InfoTaskId';
import { ChipTaskExpiration } from 'react-scale/components/scalers/tasks/OutlierTaskHeader/ChipTaskExpiration';
import SandboxProjectTag from './SandboxProjectTag';
import { IUserJson } from '@scale/shared/types/user';
import { userHasPayoutJob } from '@scale/shared/types/workerSource';
import {
  PayRateType,
  EarningRateField,
  EarningRatePayRateCardDescription,
} from '@scale/shared/defaults';
import { useRouter } from 'next/router';

import { numberToDollars } from 'react-scale/components/scalers/tasks/OutlierTaskHeader/utils';
import { toast } from 'react-toastify';
import logger from '@scale/frontend-lib/logging';
import { ChipAssessmentBonus } from './ChipAssessmentBonus';
import { PreAssessmentModal } from './PreAssessmentModal';

export enum RadioId {
  Approve = 'approve',
  SBQ = 'sbq',
  Quarantine = 'quarantine',
}

const MAX_PROJECT_NAME_LENGTH = 47;

function truncateString(str: string, maxLength: number, useWordBoundary: boolean) {
  if (str.length <= maxLength) {
    return str;
  }
  const subString = str.slice(0, maxLength - 1);
  return (useWordBoundary ? subString.slice(0, subString.lastIndexOf(' ')) : subString) + '...';
}

interface IOutlierTaskHeaderProps {
  user?: IUserJson;
  className?: string;
  config: MultiTurnInteractionConfiguration;
  taskId?: string;
  projectId?: string;
  taskDescription: string;
  autosave: AutosaveHook<any>;
  submitTooltip: string;
  canSubmit: boolean;
  canSubmitSBQ?: boolean;
  submitSBQTooltip?: string;
  isInstructionTask: boolean;
  isInitialBenchmark?: boolean;
  preventSkip?: boolean;
  isTutorialBenchmark?: boolean;
  isSubmittingTask: boolean;
  assignment: AssignmentJson;
  responses: IndexedInteractionStepResponse[];
  claim?: Claim;
  showHeader?: boolean;
  hasWarning?: boolean;
  warningParams?: IWarningParams;
  project?: IProjectJson;
  autosaveEnabled: boolean;
  taskMetadata?: any;
  isChatTaskWithComments?: boolean;
  sharingEnabled?: boolean;
  onUserSkip: () => void;
  toggleShowingShortcuts?: () => void;
  handleSubmit: SendHandler;
  getTimeSpent?: () => any;
  nextTaskHandler?: () => void;
  nextTaskOnExpiration?: () => void;
  onUserReport: (reason: string, explanation: string) => void;
  isTestQuestion: boolean;
  isReviewMode?: boolean;
  canReturnToQueue?: boolean;
  setTaskFeedback?: (
    taskFeedbackEntries: ITaskFeedbackEntryResponse[],
    callback: () => void,
  ) => void;
  startTimer?: () => void;
  pauseTimer?: () => void;
  isChanged?: boolean;
  resetToPreviousResponses?: () => void;
  taskType: TaskType;
  useMultiReplicaView?: boolean;
  onStage?: (stage: string) => void;
  staged?: RadioId | undefined;
  setPostSubmissionGraph?: (value: boolean) => void;
  canQuarantine?: boolean;
  shouldPreventSend?: boolean;
  pageNotCompleteReason?: string;
  enableUnifiedFeedback?: boolean;
}

// eslint-disable-next-line complexity
const OutlierTaskHeaderInternal = (props: IOutlierTaskHeaderProps) => {
  const router = useRouter();
  const { width } = useWindowSize();
  const reportTaskRef = useRef<ReportTaskRef | null>(null);

  /* Feature flag for pay rate chip */
  const isPayRateFlagOn = useUnleashFeatureFlag(
    'tasking-pay-rate-chip',
    { userId: props.user?._id },
    false,
  );
  /* End of feature flag for pay rate chip */
  // For tutorial practice override
  const { shouldPreventSend = false } = props;

  const { theme, setTheme } = useChatThemeStore();
  const { customerAuditShowInstructions, setCustomerAuditShowInstructions } =
    useShowContributorInstructionsStore();

  const isDrawerOpen = useChatLayoutStore(state => state.isDrawerOpen);
  const setIsDrawerOpen = useChatLayoutStore(state => state.setIsDrawerOpen);

  const [skipTaskOpen, setSkipTaskOpen] = useState(false);
  const reviewLevel = useChatStoreExtract(state => state.reviewLevel);
  const config = useChatConfig();

  const { maxClaim, payableTime } = useClaimTime(props.claim, props.projectId, props.assignment);
  const { isPaused, showPauseButton } = usePauseCapability(props.taskId ?? '');
  const isRealCustomerAudit = useIsRealCustomerAudit();
  const shouldAlwaysShowContributorInstructions = useUnleashFeatureFlag(
    'audit-always-show-contributor-instructions',
    { projectId: props.projectId },
    false,
  );

  const shouldHideDiscardChangesButton = useUnleashFeatureFlag(
    'chat-hide-discard-changes-button',
    { projectId: props.projectId },
    false,
  );

  // TODO: this should be moved to the backend and be returned via `usePayRate`
  const [timerData] = useTimerData();
  const numOfTurns = useNumberOfTurns();
  const timeSpent = timerData.serverTimeSpent / 60;
  const maxTime = getMaxPayableTime(config, numOfTurns, payableTime);
  const isOvertime = payableTime && timeSpent >= maxTime;

  const isExpired = maxClaim < new Date();

  const {
    data: payRates,
    isLoading: loadingPayRates,
    isError: errorPayRates,
  } = usePayRate(
    props.projectId,
    props.assignment.loggedUserId,
    reviewLevel,
    props.taskId,
    numOfTurns,
  );

  const [previousNumTurns, setPreviousNumTurns] = useState<number | undefined>(undefined);

  // Toast message for pay per task additional turn rate change
  useEffect(() => {
    if (loadingPayRates || errorPayRates) return;

    if (_.isNil(previousNumTurns)) {
      setPreviousNumTurns(numOfTurns);
      return;
    }

    // Check for actual change in numOfTurns
    if (!_.isNil(numOfTurns) && numOfTurns !== previousNumTurns) {
      const impressionEvent = {
        userId: props.assignment.loggedUserId?.toString() || '',
        numberTurns: numOfTurns,
        taskId: props.taskId?.toString() || '',
      };
      logEvent(Event.AddedTurnToTaskEvent, impressionEvent);

      if (payRates?.additionalPerTurnRate && numOfTurns > 1) {
        toast(
          <div className="flex gap-2">
            <p className="font-medium text-xs">
              {numberToDollars(payRates?.additionalPerTurnRate)} added to the per task rate
            </p>
          </div>,
          {
            position: 'top-left',
            autoClose: 10000,
            theme: 'dark',
          },
        );
      }
      setPreviousNumTurns(numOfTurns);
    }
  }, [
    numOfTurns,
    payRates,
    loadingPayRates,
    errorPayRates,
    previousNumTurns,
    props.taskId,
    props.assignment.loggedUserId,
  ]);

  const payRateType = payRates?.payType;

  // investigation (12/6): add logging if we're showing the expiration modal for a non-expiring wall BM
  useEffect(() => {
    if (isExpired && payRateType === PayRateType.HourlyTraining) {
      logger.error(`Showing expiration modal for non-expiring wall BM`, {
        claim: props.claim ? JSON.parse(JSON.stringify(props.claim)) : undefined,
        maxClaim,
        taskId: props.taskId,
        assignmentId: props.assignment.assignmentId,
      });
    }
  }, [isExpired, maxClaim, payRateType, props.assignment.assignmentId, props.claim, props.taskId]);

  const isPayPerTask =
    payRateType === PayRateType.PayPerTask || payRateType === PayRateType.PayPerTaskTraining;
  const isSingleTaskPay = payRateType === PayRateType.HLE;

  const [stayOnTask, setStayOnTask] = useState(false);

  const projectName = props.assignment.projectName ?? '';
  const {
    handleShare,
    isLoading: isShareLoading,
    isModalOpen: isShareModalOpen,
    slug: chatSlug,
    setModalOpen: setShareModalOpen,
  } = useChatShare({
    config: props.config,
    responses: props.responses,
    context: {
      reviewLevel,
      projectId: props.projectId,
    },
    taskId: props.taskId,
    projectId: props.projectId,
  });

  const {
    enableSensitiveContentReporting,
    enableHighRiskSensitiveContentReporting,
    useBulbaSensitiveContentReporting,
  } = useSensitiveContentReporting(
    props.project!,
    props.assignment.type,
    props.project?.taskCustomer,
  );

  const toggleProjectOutline = useProjectOutlineStore(state => state.toggleProjectOutline);
  const projectOutlineIsVisible = useProjectOutlineStore(store => store.isVisible);

  // don't show new header if overtime rate is 0 or undefined
  const enableOvertimePay = !!payRates?.overtimeRate;

  // we should show outlier v2 header if overtime is enabled or it is pay per task / pay per single task
  const showOutlierV2Header = enableOvertimePay || isPayPerTask || isSingleTaskPay;
  // TODO: Also remove this once we handle off-platform pay better

  // Hide skip task button for initial benchmark tasks
  const hideSkipTaskButton =
    (useUnleashFeatureFlag(
      'outlier-hide-initial-benchmark-skip-task-button',
      { projectId: props.projectId },
      false,
    ) &&
      props.isInitialBenchmark) ||
    props.preventSkip;

  const wallBMHeader = useUnleashFeatureFlag(
    'wall-benchmark-timer-ui',
    { projectId: props.projectId },
    false,
  );

  // Reporting for sensitive content is a form of skipping, we should disable it for wall benchmarks too
  const enableReporting =
    !hideSkipTaskButton &&
    (enableSensitiveContentReporting || enableHighRiskSensitiveContentReporting);

  const canSubmitSBQ = props.canSubmitSBQ ?? true;
  const submitTooltipSBQ = props.submitSBQTooltip || props.submitTooltip;

  if (!props.showHeader) return null;

  const onClickRRSWrapper = (onClick: () => void, reviewType: FeedbackConditions) => {
    const onClickFn = (
      textReasons: Record<string, string>,
      selectReasons: Record<string, string[]>,
    ) => {
      const taskFeedbackEntries: ITaskFeedbackEntryResponse[] = [];

      for (const entry of props.assignment?.feedbackTemplate?.[reviewType] ?? []) {
        const title: string = entry.title;
        const inputType: string = entry.inputType;
        if (inputType === TaskFeedbackInput.Text) {
          const entryResponse: ITaskFeedbackEntryResponse = {
            title,
            response: textReasons[title],
          };
          taskFeedbackEntries.push(entryResponse);
        } else {
          const entryResponse: ITaskFeedbackEntryResponse = {
            title,
            response: selectReasons[title],
          };
          taskFeedbackEntries.push(entryResponse);
        }
      }
      if (props.setTaskFeedback !== undefined) {
        props.setTaskFeedback(taskFeedbackEntries, onClick);
      } else {
        onClick();
      }
    };

    return onClickFn;
  };

  const handleProjectOutline = () => {
    const eventPayload = {
      pageLoadId: getPageLoadSingleton(),
      onboardingState: 'training',
    };
    if (projectOutlineIsVisible) {
      analytics.track('Project onboarding', {
        ...eventPayload,
        event: 'outline_closed',
      });
    } else {
      analytics.track('Project onboarding', {
        ...eventPayload,
        event: 'outline_opened',
      });
    }

    toggleProjectOutline();
  };

  const renderStagingButtons = () => {
    const approveOption = {
      id: RadioId.Approve,
      title: props.isChanged
        ? TEXTS[SendTypes.ApproveWithChanges][SendStatus.Send] ?? ''
        : TEXTS[SendTypes.Approve][SendStatus.Send] ?? '',
    };
    const rejectOption = {
      id: RadioId.SBQ,
      title: TEXTS[SendTypes.SendBackToQueue][SendStatus.Send] ?? '',
    };
    const quarantineOption = {
      id: RadioId.Quarantine,
      title: 'Quarantine',
    };
    const options = [approveOption];
    if (props.canReturnToQueue) {
      options.push(rejectOption);
    }
    if (props.canQuarantine) {
      options.push(quarantineOption);
    }

    return (
      <Tooltip
        text={props.pageNotCompleteReason ?? ''}
        asChild={false}
        target={
          <RadioGroup
            key={`stage-${props.taskId}`}
            className=""
            handleSelect={function noRefCheck() {}} // deprecated
            onSelect={id => props.onStage!(id)} // call props.onchange here
            options={options}
            selectedId={props.staged}
            isDisabled={!props.canSubmit}
          />
        }
      />
    );
  };

  const renderRejectButton = () => {
    return (
      <SendTaskModal
        key={`reject-${props.taskId}`}
        disableModal
        type={SendTypes.SendBackToQueue}
        sendHandler={props.handleSubmit}
        getTimeSpent={props.getTimeSpent}
        nextTaskHandler={props.nextTaskHandler}
        taskType={props.taskType}
        taskProjectId={props.projectId}
        autoloadNextAssignment={!props.isTestQuestion}
        taskUserId={(props.project && props.project.taskCustomer) || ''}
        taskId={props.taskId}
        assignmentId={props.assignment.assignmentId}
        reviewLevel={reviewLevel}
        shouldPreventSend={shouldPreventSend}
      >
        {({ disabled, onClick, text }) => {
          const onClickWithConfirmation = () => {
            if (
              !props.canSubmit &&
              !window.confirm('Are you sure you want to return this task to the queue?')
            ) {
              return;
            }
            onClick();
          };
          if (
            hasFeedbackTemplateForType(FeedbackConditions.SBTQ, props.assignment.feedbackTemplate)
          ) {
            const filteredEntries: ITaskFeedbackEntry[] = filterEntriesByReviewLevel(
              props.assignment?.feedbackTemplate?.sbtq ?? [],
              props.assignment.reviewLevel,
            );
            if (filteredEntries.length) {
              return (
                <Tooltip
                  text={submitTooltipSBQ}
                  asChild={false}
                  target={
                    <TemplateFeedback
                      handleSubmitFeedback={onClickRRSWrapper(
                        onClickWithConfirmation,
                        FeedbackConditions.SBTQ,
                      )}
                      feedbackEntries={filteredEntries}
                      buttonText={text}
                      confirmButtonText={text}
                      disabled={disabled}
                      modernButtonStyle={true}
                      variant="danger"
                    />
                  }
                  delayDuration={0}
                />
              );
            }
          }

          return (
            <Tooltip
              text={submitTooltipSBQ}
              asChild={false}
              target={
                <Button
                  type="button"
                  variant="danger"
                  disabled={!canSubmitSBQ || disabled}
                  onClick={onClickWithConfirmation}
                  aria-label={text}
                >
                  {text}
                </Button>
              }
              delayDuration={0}
            />
          );
        }}
      </SendTaskModal>
    );
  };

  const renderAcceptWithChangesButton = () => {
    return (
      <SendTaskModal
        key={`acceptwithchanges-${props.taskId}`}
        disableModal
        type={SendTypes.ApproveWithChanges}
        sendHandler={props.handleSubmit}
        getTimeSpent={props.getTimeSpent}
        nextTaskHandler={props.nextTaskHandler}
        taskType={TaskType.Chat}
        taskProjectId={props.projectId}
        autoloadNextAssignment={!props.isTestQuestion}
        taskUserId={(props.project && props.project.taskCustomer) || ''}
        taskId={props.taskId}
        assignmentId={props.assignment.assignmentId}
        reviewLevel={reviewLevel}
        shouldPreventSend={shouldPreventSend}
      >
        {({ disabled, onClick, text }) => {
          if (
            hasFeedbackTemplateForType(
              FeedbackConditions.MakeChanges,
              props.assignment.feedbackTemplate,
            )
          ) {
            const filteredEntries: ITaskFeedbackEntry[] = filterEntriesByReviewLevel(
              props.assignment?.feedbackTemplate?.makeChanges ?? [],
              props.assignment.reviewLevel,
            );
            if (filteredEntries.length) {
              return (
                <Tooltip
                  text={props.submitTooltip}
                  asChild={false}
                  target={
                    <TemplateFeedback
                      handleSubmitFeedback={onClickRRSWrapper(
                        onClick,
                        FeedbackConditions.MakeChanges,
                      )}
                      feedbackEntries={filteredEntries}
                      buttonText={text}
                      confirmButtonText={text}
                      disabled={!props.canSubmit || disabled}
                      modernButtonStyle={true}
                      variant="success"
                    />
                  }
                  delayDuration={0}
                />
              );
            }
          }

          return (
            <Tooltip
              text={props.submitTooltip}
              asChild={false}
              target={
                <Button
                  type="button"
                  variant="success"
                  disabled={!props.canSubmit || disabled}
                  onClick={onClick}
                  aria-label={text}
                >
                  {text}
                </Button>
              }
              delayDuration={0}
            />
          );
        }}
      </SendTaskModal>
    );
  };

  const renderAcceptButton = () => {
    return (
      <SendTaskModal
        key={`accept-${props.taskId}`}
        disableModal
        type={SendTypes.Approve}
        sendHandler={props.handleSubmit}
        getTimeSpent={props.getTimeSpent}
        nextTaskHandler={props.nextTaskHandler}
        taskType={props.taskType}
        taskProjectId={props.projectId}
        autoloadNextAssignment={!props.isTestQuestion}
        taskUserId={(props.project && props.project.taskCustomer) || ''}
        taskId={props.taskId}
        assignmentId={props.assignment.assignmentId}
        reviewLevel={reviewLevel}
        shouldPreventSend={shouldPreventSend}
      >
        {({ disabled, onClick, text }) => {
          if (
            hasFeedbackTemplateForType(FeedbackConditions.Accept, props.assignment.feedbackTemplate)
          ) {
            const filteredEntries: ITaskFeedbackEntry[] = filterEntriesByReviewLevel(
              props.assignment?.feedbackTemplate?.accept ?? [],
              props.assignment.reviewLevel,
            );
            if (filteredEntries.length) {
              return (
                <Tooltip
                  text={props.submitTooltip}
                  asChild={false}
                  target={
                    <TemplateFeedback
                      handleSubmitFeedback={onClickRRSWrapper(onClick, FeedbackConditions.Accept)}
                      feedbackEntries={filteredEntries}
                      buttonText={text}
                      confirmButtonText={text}
                      disabled={!props.canSubmit || disabled}
                      modernButtonStyle={true}
                      variant="success"
                    />
                  }
                  delayDuration={0}
                />
              );
            }
          }

          return (
            <Tooltip
              text={props.submitTooltip}
              asChild={false}
              target={
                <Button
                  type="button"
                  variant="success"
                  disabled={!props.canSubmit || disabled}
                  onClick={onClick}
                  aria-label={text}
                >
                  {text}
                </Button>
              }
              delayDuration={0}
            />
          );
        }}
      </SendTaskModal>
    );
  };

  const renderReviewButtons = () => {
    return (
      <TaskHeader.ButtonGroup>
        {props.canReturnToQueue && <>{renderRejectButton()}</>}
        {props.isChanged ? renderAcceptWithChangesButton() : renderAcceptButton()}
      </TaskHeader.ButtonGroup>
    );
  };

  const renderPayRateChip = ({ allowOverTime = true }) => {
    // Don't show pay rate chip if pay rate is not available
    if (
      loadingPayRates ||
      errorPayRates ||
      !payRates ||
      _.isNil(payRates.standardRate) ||
      payRates.standardRate <= 0
    ) {
      return null;
    }

    // Don't show pay rate chip for non-payout workers
    if (!userHasPayoutJob(props.user, true)) {
      return null;
    }

    // if the pay rate is HLE and already paid and no quality incentive rate, don't show the pay rate chip
    if (payRateType === PayRateType.HLE && payRates.alreadyPaid && !payRates.qualityIncentiveRate) {
      return null;
    }

    // Exclude overtime rate from feature flag
    const isPayRateOvertime =
      payRateType === PayRateType.Hourly &&
      isOvertime &&
      allowOverTime &&
      !_.isNil(payRates.overtimeRate) &&
      payRates.overtimeRate;

    // Don't show pay rate chip if FF not on unless it's overtime
    if (!isPayRateFlagOn && !isPayRateOvertime) {
      return null;
    }

    const { overtimeRate, standardRate, alreadyPaid, qualityIncentiveRate } = payRates;

    const isPaidTimeExceeded = isOvertime && allowOverTime;
    const standardRateInDollars = numberToDollars(standardRate);
    const qualityIncentiveRateInDollars = qualityIncentiveRate
      ? numberToDollars(qualityIncentiveRate)
      : undefined;

    let payRateChipText = undefined;
    let secondaryPayRateChipText = undefined;
    let tooltipText = undefined;
    let secondaryTooltipText = undefined;

    switch (payRateType) {
      case PayRateType.Hourly: {
        if (isPaidTimeExceeded) {
          if (!_.isNil(overtimeRate) && overtimeRate) {
            payRateChipText = `Exceeded Time Rate: ${numberToDollars(overtimeRate)}/hr`;
          }
        } else {
          payRateChipText = `Task Rate: ${standardRateInDollars}/hr`;
        }
        break;
      }
      case PayRateType.HourlyTraining: {
        if (isPaidTimeExceeded) {
          if (!_.isNil(overtimeRate) && overtimeRate) {
            payRateChipText = `Exceeded Time Rate: ${numberToDollars(overtimeRate)}/hr`;
          }
        } else {
          payRateChipText = `Assessment Rate: ${standardRateInDollars}/hr`;
        }
        break;
      }
      case PayRateType.PayPerTask: {
        payRateChipText = `Task Rate: ${standardRateInDollars}/task`;
        break;
      }
      case PayRateType.PayPerTaskTraining: {
        payRateChipText = `Assessment Rate: ${standardRateInDollars}/task`;
        break;
      }
      case PayRateType.HLE: {
        // if alreadyPaid, show that it's already been paid
        if (alreadyPaid) {
          payRateChipText = `${EarningRateField.SingleTaskPayQualityIncentive}: ${qualityIncentiveRateInDollars}/task`;
          tooltipText = `This attempt may increase your variable pay. Your upfront ${standardRateInDollars} payment is already complete and unaffected by this attempt.`;
          secondaryPayRateChipText = `${EarningRateField.SingleTaskPayBaseRate}: ${standardRateInDollars}/task (paid)`;
          secondaryTooltipText = `You earned ${standardRateInDollars} for your first attempt. Subsequent attempts are not paid but can affect variable pay for the task.`;
        } else {
          payRateChipText = `${EarningRateField.SingleTaskPayBaseRate}: ${standardRateInDollars}/task`;
          tooltipText = EarningRatePayRateCardDescription[EarningRateField.SingleTaskPayBaseRate];
          if (qualityIncentiveRateInDollars) {
            secondaryPayRateChipText = `${EarningRateField.SingleTaskPayQualityIncentive}: ${qualityIncentiveRateInDollars}/task`;
            secondaryTooltipText =
              EarningRatePayRateCardDescription[EarningRateField.SingleTaskPayQualityIncentive];
          }
        }
        break;
      }
    }

    if (_.isNil(payRateChipText)) {
      return null;
    }

    const renderBadge = (
      text: string,
      variant: 'warning' | 'secondary' | 'info',
      tooltipText?: string,
    ) => {
      const tagContent = (
        <Tag title={tooltipText || text} variant={variant}>
          {text}
        </Tag>
      );

      return tagContent;
    };

    const getVariant = (isSecondary = false) => {
      if (isPaidTimeExceeded && !isSecondary) return 'warning';
      if (
        payRateType === PayRateType.HourlyTraining ||
        payRateType === PayRateType.PayPerTaskTraining
      )
        return 'info';
      return 'secondary';
    };

    return (
      <div className="flex flex-row gap-1 items-center">
        {renderBadge(payRateChipText, getVariant(), tooltipText)}
        {!!secondaryPayRateChipText &&
          renderBadge(secondaryPayRateChipText, 'secondary', secondaryTooltipText)}
      </div>
    );
  };

  const renderAttemptButtons = () => {
    return (
      <TaskHeader.ButtonGroup>
        <SendTaskModal
          disableModal
          type={SendTypes.Submit}
          sendHandler={props.handleSubmit}
          getTimeSpent={props.getTimeSpent}
          nextTaskHandler={props.nextTaskHandler}
          taskType={props.taskType}
          taskProjectId={props.projectId}
          autoloadNextAssignment={!props.isTestQuestion}
          taskUserId={(props.project && props.project.taskCustomer) || ''}
          setPostSubmissionGraph={props.setPostSubmissionGraph}
          taskId={props.taskId}
          assignmentId={props.assignment.assignmentId}
          reviewLevel={reviewLevel}
          shouldPreventSend={shouldPreventSend}
        >
          {({ disabled, onClick, text }) => (
            <Tooltip
              text={props.submitTooltip}
              asChild={false}
              side="left"
              target={
                <Button
                  type="button"
                  variant="primary"
                  disabled={!props.canSubmit || disabled}
                  onClick={onClick}
                  data-testid={`submit-task-button`}
                  aria-label={text}
                >
                  {text}
                </Button>
              }
              delayDuration={0}
            />
          )}
        </SendTaskModal>
      </TaskHeader.ButtonGroup>
    );
  };

  const pauseModals = (
    <>
      {isPaused && !isExpired && (
        <PauseModal
          claim={props.claim}
          taskId={props.taskId}
          projectId={props.projectId}
          assignment={props.assignment}
        />
      )}

      {isExpired && (
        <TaskHoursExceededModal
          nextTaskOnExpirationHandler={props.nextTaskOnExpiration}
          payRateType={payRateType as PayRateType}
        />
      )}

      {!loadingPayRates && payRates && isOvertime && !stayOnTask && !isExpired && (
        <PaidTimeExceededModal
          overtimeRate={numberToDollars(payRates?.overtimeRate ?? 0)}
          onStayOnTask={() => setStayOnTask(true)}
          claim={props.claim}
          payRateType={payRateType as PayRateType}
          projectId={props.projectId}
          assignment={props.assignment}
          taskId={props.taskId}
        />
      )}
    </>
  );

  const maybeTruncatedProjectName = (
    <div>
      <Typography.Subtitle2>
        {truncateString(projectName, MAX_PROJECT_NAME_LENGTH, true)}{' '}
      </Typography.Subtitle2>
    </div>
  );

  // for wall BMs, show new header if:
  // 1. BM expires or
  // 2. BM is not an initial wall BM
  const bmWallFeature = wallBMHeader || !props.isInitialBenchmark;
  if (showOutlierV2Header && userHasPayoutJob(props.user, true) && bmWallFeature) {
    return (
      <>
        <ReportTask
          ref={reportTaskRef}
          disabled={props.isSubmittingTask}
          avoidButtonRendering
          handleReport={props.onUserReport}
          enableSensitiveContentReporting={enableSensitiveContentReporting}
          enableHighRiskSensitiveContentReporting={enableHighRiskSensitiveContentReporting}
          useBulbaSensitiveContentReporting={useBulbaSensitiveContentReporting}
        />
        <SkipTask
          taskId={props.taskId}
          open={skipTaskOpen}
          onOpenChange={newOpen => setSkipTaskOpen(newOpen)}
          handleSkip={props.onUserSkip}
          isInstructionTask={props.isInstructionTask}
          avoidButtonRendering
        />
        <ChatShareModal
          isModalOpen={isShareModalOpen}
          setModalOpen={setShareModalOpen}
          slug={chatSlug}
        />
        {props.isInitialBenchmark && !loadingPayRates && (
          <PreAssessmentModal
            userId={props.assignment.loggedUserId ?? ''}
            assessmentBonus={payRates?.payPerTaskAssessmentPassBonusRate}
          />
        )}
        <ProjectOnboardingOutline
          projectId={props.projectId}
          reviewLevel={reviewLevel}
          claim={props.claim}
          displayHovered
        />
        {pauseModals}
        <TaskHeader className={twMerge('sticky top-0 z-40 items-center', props.className)}>
          <TaskHeader.Left>
            <div className="flex items-center gap-6">
              {<TaskHeader.Icon icon={['far', 'list']} onClick={handleProjectOutline} />}
              <OutlierLogo className="mr-4 h-7" />
            </div>

            <div className="flex flex-col">
              <div className="flex gap-2 items-center">
                {props.isInitialBenchmark && (
                  <Tooltip
                    target={
                      <Tag className="mx-2" variant="neutral">
                        <div className="flex flex-row gap-1 items-center">
                          <FontAwesomeIcon icon={['fas', 'info-circle']} />{' '}
                          <div>{props.isTutorialBenchmark ? 'Training' : 'Assessment'} Task</div>
                        </div>
                      </Tag>
                    }
                    text={
                      <div className="flex flex-col gap-1">
                        <div>
                          {props.isTutorialBenchmark
                            ? "Training Tasks are focused on providing examples to help you learn better about the project's quality requirements."
                            : "Assessment Tasks are focused on assessing whether you meet the project's quality requirements."}
                        </div>
                        <div>
                          They are paid at a lower rate, please refer to the the earnings rate card
                          for more details.
                        </div>
                      </div>
                    }
                    delayDuration={0}
                    asChild={false}
                  />
                )}
                {projectName.length > MAX_PROJECT_NAME_LENGTH ? (
                  <Tooltip
                    delayDuration={0}
                    target={<div>{maybeTruncatedProjectName}</div>}
                    text={projectName}
                  />
                ) : (
                  maybeTruncatedProjectName
                )}
                {props.taskId && (
                  <Tooltip
                    delayDuration={0}
                    target={
                      <div className="flex gap-2 items-center">
                        <Typography.Subtitle2>
                          <span className="text-xs font-normal">{props.taskId!}</span>
                        </Typography.Subtitle2>
                        <FontAwesomeIcon
                          className="cursor-pointer text-neutral-700"
                          icon={['fas', 'copy']}
                          onClick={() => navigator.clipboard.writeText(props.taskId!)}
                        />
                      </div>
                    }
                    text={`Copy Task ID: ${props.taskId}`}
                  />
                )}
                <InfoTurns additionalMinutesPerTurn={payableTime?.additionalMinutesPerTurn} />
                <ChipPayTier projectId={props.assignment.projectId} />
                <ChipQualityPay
                  projectId={props.assignment.projectId}
                  reviewLevel={props.assignment.reviewLevel}
                  assignmentId={props.assignment.assignmentId}
                />
              </div>
              <div className="flex gap-4 items-center">
                <InfoDeadline
                  claim={props.claim}
                  newHeader={enableOvertimePay}
                  projectId={props.projectId}
                  assignment={props.assignment}
                  taskId={props.taskId}
                />
                {!isPayPerTask && (
                  <InfoTimeSpent
                    claim={props.claim}
                    taskId={props.taskId}
                    taskType={props.taskType}
                    projectId={props.projectId}
                    assignment={props.assignment}
                    isSubmittingTask={props.isSubmittingTask}
                  />
                )}
                {!isExpired && !!payableTime && showPauseButton && !isPayPerTask && (
                  <PauseButton taskId={props.taskId} />
                )}
                {renderPayRateChip({ allowOverTime: true })}
                {props.isInitialBenchmark && (
                  <ChipAssessmentBonus
                    assessmentBonus={payRates?.payPerTaskAssessmentPassBonusRate}
                  />
                )}
                <SandboxProjectTag projectId={props.projectId} />
              </div>
            </div>
          </TaskHeader.Left>
          <TaskHeader.Right className="gap-2">
            <InfoAutosave autosave={props.autosave} autosaveEnabled={props.autosaveEnabled} />
            {isRealCustomerAudit && !shouldAlwaysShowContributorInstructions && (
              <Switch
                label="Show contributor-facing instructions"
                checked={customerAuditShowInstructions}
                onCheckedChange={value => {
                  setCustomerAuditShowInstructions(value);
                }}
                className="mr-4"
              />
            )}
            {!_.isNil(props.projectId) && (
              <Button
                icon={{
                  position: 'left',
                  type: ['fas', 'book'],
                  variant: 'white',
                }}
                variant="white"
                size="sm"
                onClick={() => {
                  const isExpertPage = window.location.pathname.includes('/expert');

                  const path = isExpertPage
                    ? `/expert/instruction/${props.projectId}`
                    : `/instruction/${props.projectId}`;
                  let instructionPageUrl = window.location.origin + path;

                  if (props.assignment?.assignmentId) {
                    instructionPageUrl += `?assignmentId=${props.assignment.assignmentId}`;
                  }

                  window.open(instructionPageUrl, '_blank');

                  const data = {
                    isNewInstruction: true,
                    isOnboardingFlow: null,
                    isDuringTasking: true,
                    openTabButtonClicked: null,
                    startTime: new Date().getTime(),
                    closeTime: null,
                  };

                  logEvent(Event.InstructionRenderRecord, data);
                }}
              >
                Instructions
              </Button>
            )}
            <Button
              icon={{
                type: ['fas', 'thumbtack'],
                variant: 'white',
                position: 'left',
              }}
              variant="white"
              size="sm"
              onClick={() => setIsDrawerOpen(!isDrawerOpen)}
              className={isDrawerOpen ? 'bg-neutral-50' : ''}
            >
              Pinned Items
            </Button>
            <TaskHeader.IconHandler direction="bottom-left">
              <IconInlineFeedback
                isDefaultOpen={!props.enableUnifiedFeedback}
                visible
                isChatTaskWithComments={props.isChatTaskWithComments}
                userId={props.assignment.loggedUserId}
                isNewHeader
                enableUnifiedFeedback={props.enableUnifiedFeedback}
              />
              {props.sharingEnabled && (
                <TaskHeader.IconHandlerItem
                  icon={['far', 'share']}
                  onClick={handleShare}
                  disabled={isShareLoading}
                  label="Share Session"
                />
              )}
              <IconTriggerAutosave
                visible={width > 560}
                autosaveEnabled={props.autosaveEnabled}
                triggerAutosave={props.autosave.flush}
                isAutosaving={props.autosave.isAutosaving}
              />

              {props.toggleShowingShortcuts && (
                <TaskHeader.IconHandlerItem
                  icon="keyboard"
                  onClick={props.toggleShowingShortcuts}
                  label="Shortcuts"
                />
              )}
              {!isRealCustomerAudit && enableReporting && (
                <TaskHeader.IconHandlerItem
                  label="Report Sensitive Content"
                  icon={['far', 'flag-alt']}
                  disabled={reportTaskRef.current?.disabled}
                  onClick={reportTaskRef.current?.openModal}
                />
              )}
              {!isRealCustomerAudit && !shouldHideDiscardChangesButton && (
                <TaskHeader.IconHandlerItem
                  label="Discard Changes"
                  icon={['far', 'undo']}
                  disabled={!props.isChanged}
                  onClick={() => {
                    if (
                      props.resetToPreviousResponses &&
                      window.confirm('Are you sure you want to reset to the previous response?')
                    ) {
                      props.resetToPreviousResponses();
                    }
                  }}
                />
              )}
              {!isRealCustomerAudit && !hideSkipTaskButton && (
                <TaskHeader.IconHandlerItem
                  label="Skip Task"
                  icon={['far', 'forward']}
                  onClick={() => {
                    setSkipTaskOpen(true);
                  }}
                />
              )}
              <TaskHeader.IconHandlerItem
                label={theme === ChatTheme.Dark ? 'Toggle Light Mode' : 'Toggle Dark Mode'}
                icon={theme === ChatTheme.Dark ? 'moon' : 'sun'}
                onClick={() => {
                  setTheme(theme === ChatTheme.Dark ? ChatTheme.Light : ChatTheme.Dark);
                }}
              />
            </TaskHeader.IconHandler>
            <TaskHeader.Divider className="mx-2" />
            {props.isReviewMode
              ? props.useMultiReplicaView
                ? renderStagingButtons()
                : renderReviewButtons()
              : renderAttemptButtons()}
          </TaskHeader.Right>
        </TaskHeader>
      </>
    );
  } else {
    return (
      <>
        {isPaused && (
          <div className="fixed top-0 left-0 w-full h-full bg-neutral-0/[0.95] z-30 flex justify-center items-center">
            <div className="text-center">
              <Typography.H4>Task is paused</Typography.H4>
              <Typography.Paragraph3>
                All interactions are disabled until the task is resumed.
              </Typography.Paragraph3>
            </div>
          </div>
        )}
        <ReportTask
          ref={reportTaskRef}
          disabled={props.isSubmittingTask}
          avoidButtonRendering
          handleReport={props.onUserReport}
          enableSensitiveContentReporting={enableSensitiveContentReporting}
          enableHighRiskSensitiveContentReporting={enableHighRiskSensitiveContentReporting}
          useBulbaSensitiveContentReporting={useBulbaSensitiveContentReporting}
        />
        <SkipTask
          open={skipTaskOpen}
          taskId={props.taskId}
          onOpenChange={newOpen => setSkipTaskOpen(newOpen)}
          handleSkip={props.onUserSkip}
          isInstructionTask={props.isInstructionTask}
          avoidButtonRendering
        />
        <ChatShareModal
          isModalOpen={isShareModalOpen}
          setModalOpen={setShareModalOpen}
          slug={chatSlug}
        />
        {props.isInitialBenchmark && !loadingPayRates && (
          <PreAssessmentModal
            userId={props.assignment.loggedUserId ?? ''}
            assessmentBonus={payRates?.payPerTaskAssessmentPassBonusRate}
          />
        )}
        <ProjectOnboardingOutline
          projectId={props.projectId}
          reviewLevel={reviewLevel}
          claim={props.claim}
          displayHovered
        />
        <TaskHeader className={twMerge('sticky top-0 z-40', props.className)}>
          <TaskHeader.Left>
            <div className="flex items-center gap-3 mr-4">
              {<TaskHeader.Icon icon="sidebar" onClick={handleProjectOutline} />}
              <button
                onClick={() => {
                  router.push('/expert');
                }}
              >
                <img
                  src="https://static.remotasks.com/uploads/602b25a6e0984c00343d3b26/Frame.png"
                  alt="Outlier"
                  className="w-8 h-8 mr-4"
                />
              </button>
            </div>

            {/*
            Even if a project has an internal project taskerName it does not get passed in through props.
            Uncomment this Header once that gets implemented.
            {props.project?.taskerName && (
              <TaskHeader.Heading className="mr-4">{props.project.taskerName}</TaskHeader.Heading>
            )}
            */}
            <TaskHeader.InfoHandler>
              <TaskHeader.InfoHandlerItem title="Description">
                {props.taskDescription}
              </TaskHeader.InfoHandlerItem>
              <OldInfoAutosave
                visible={width > 560}
                autosaveEnabled={props.autosaveEnabled}
                lastAutosaveTime={props.autosave.lastAutosaveTime}
              />
              <InfoDeadline visible={width > 760} claim={props.claim} taskId={props.taskId} />
              <InfoIdleTime claim={props.claim} />
              <InfoTaskId taskId={props.taskId} taskMetadata={props.taskMetadata} />
            </TaskHeader.InfoHandler>
            <ChipPayTier projectId={props.assignment.projectId} />
            <ChipQualityPay
              projectId={props.assignment.projectId}
              reviewLevel={props.assignment.reviewLevel}
              assignmentId={props.assignment.assignmentId}
            />
            {!isPayPerTask && <ChipTaskExpiration className="ml-4" claim={props.claim} />}
            {renderPayRateChip({ allowOverTime: false })}
            {props.isInitialBenchmark && (
              <ChipAssessmentBonus assessmentBonus={payRates?.payPerTaskAssessmentPassBonusRate} />
            )}
            <SandboxProjectTag projectId={props.projectId} />
            {props.isInitialBenchmark && (
              <Tooltip
                target={
                  <Tag className="mx-2" variant="neutral">
                    <div className="flex flex-row gap-1 items-center">
                      <FontAwesomeIcon icon={['fas', 'info-circle']} />{' '}
                      <div>{props.isTutorialBenchmark ? 'TRAINING' : 'ASSESSMENT'} TASK</div>
                    </div>
                  </Tag>
                }
                text={
                  <div className="flex flex-col gap-1">
                    <div>
                      {props.isTutorialBenchmark
                        ? "Training Tasks are focused on providing examples to help you learn better about the project's quality requirements."
                        : "Assessment Tasks are focused on assessing whether you meet the project's quality requirements."}
                    </div>
                    <div>
                      They are paid at a lower rate, please refer to the the earnings rate card for
                      more details.
                    </div>
                  </div>
                }
                delayDuration={0}
                asChild={false}
              />
            )}
            {showPauseButton && !isPayPerTask && (
              <div className="ml-4">
                <PauseButton
                  taskId={props.taskId}
                  pauseTimer={props.pauseTimer}
                  startTimer={props.startTimer}
                />
              </div>
            )}
          </TaskHeader.Left>
          <TaskHeader.Right>
            {isRealCustomerAudit && !shouldAlwaysShowContributorInstructions && (
              <Switch
                label="Show contributor-facing instructions"
                checked={customerAuditShowInstructions}
                onCheckedChange={value => {
                  setCustomerAuditShowInstructions(value);
                }}
                className="mr-4"
              />
            )}
            <TaskHeader.IconHandler direction="bottom-left">
              <IconInlineFeedback
                isDefaultOpen={!props.enableUnifiedFeedback}
                visible
                isChatTaskWithComments={props.isChatTaskWithComments}
                userId={props.assignment.loggedUserId}
                enableUnifiedFeedback={props.enableUnifiedFeedback}
              />
              {props.sharingEnabled && (
                <TaskHeader.IconHandlerItem
                  icon={['far', 'share']}
                  onClick={handleShare}
                  disabled={isShareLoading}
                  label="Share Session"
                />
              )}
              <IconTriggerAutosave
                visible={width > 560}
                autosaveEnabled={props.autosaveEnabled}
                triggerAutosave={props.autosave.flush}
                isAutosaving={props.autosave.isAutosaving}
              />
              <TaskHeader.IconHandlerItem
                visible
                icon="thumbtack"
                onClick={() => setIsDrawerOpen(!isDrawerOpen)}
                label="Pinned Items"
              />
              {!_.isNil(props.projectId) && (
                <TaskHeader.IconHandlerItem
                  visible={width > 600}
                  icon="books"
                  onClick={() => {
                    const isExpertPage = window.location.pathname.includes('/expert');

                    const path = isExpertPage
                      ? `/expert/instruction/${props.projectId}`
                      : `/instruction/${props.projectId}`;
                    let instructionPageUrl = window.location.origin + path;

                    if (props.assignment?.assignmentId) {
                      instructionPageUrl += `?assignmentId=${props.assignment.assignmentId}`;
                    }

                    window.open(instructionPageUrl, '_blank');

                    const data = {
                      isNewInstruction: true,
                      isOnboardingFlow: null,
                      isDuringTasking: true,
                      openTabButtonClicked: null,
                      startTime: new Date().getTime(),
                      closeTime: null,
                    };

                    logEvent(Event.InstructionRenderRecord, data);
                  }}
                  label="Instructions"
                />
              )}
              {props.toggleShowingShortcuts && (
                <TaskHeader.IconHandlerItem
                  icon="keyboard"
                  onClick={props.toggleShowingShortcuts}
                  label="Shortcuts"
                />
              )}
              {!isRealCustomerAudit && enableReporting && (
                <TaskHeader.IconHandlerItem
                  label="Report Sensitive Content"
                  icon={['far', 'flag-alt']}
                  disabled={reportTaskRef.current?.disabled}
                  onClick={reportTaskRef.current?.openModal}
                />
              )}
              {!isRealCustomerAudit && !shouldHideDiscardChangesButton && (
                <TaskHeader.IconHandlerItem
                  label="Discard Changes"
                  icon={['far', 'undo']}
                  disabled={!props.isChanged}
                  onClick={() => {
                    if (
                      props.resetToPreviousResponses &&
                      window.confirm('Are you sure you want to reset to the previous response?')
                    ) {
                      props.resetToPreviousResponses();
                    }
                  }}
                />
              )}
              {!isRealCustomerAudit && !hideSkipTaskButton && (
                <TaskHeader.IconHandlerItem
                  label="Skip Task"
                  icon={['far', 'forward']}
                  onClick={() => {
                    setSkipTaskOpen(true);
                  }}
                />
              )}
              <TaskHeader.IconHandlerItem
                label={theme === ChatTheme.Dark ? 'Toggle Light Mode' : 'Toggle Dark Mode'}
                icon={theme === ChatTheme.Dark ? 'moon' : 'sun'}
                onClick={() => {
                  setTheme(theme === ChatTheme.Dark ? ChatTheme.Light : ChatTheme.Dark);
                }}
              />
            </TaskHeader.IconHandler>
            <TaskHeader.Divider />
            {props.isReviewMode
              ? props.useMultiReplicaView
                ? renderStagingButtons()
                : renderReviewButtons()
              : renderAttemptButtons()}
          </TaskHeader.Right>
        </TaskHeader>
      </>
    );
  }
};

const OutlierTaskHeader = (props: IOutlierTaskHeaderProps) => {
  const queryClientV4 = useTaskQueryClientV4(props.taskId);

  return (
    <QueryClientProviderV4 client={queryClientV4}>
      <style>{`
        /* Apply top margin to first toast when OutlierTaskHeader is present */
        body:has(.main-task-header) .Toastify__toast-container--top-left .Toastify__toast:first-child,
        body:has(.main-task-header) .Toastify__toast-container--top-right .Toastify__toast:first-child {
          margin-top: 80px;
        }
      `}</style>
      <OutlierTaskHeaderInternal
        {...props}
        className={`${props.className || ''} main-task-header`.trim()}
      />
    </QueryClientProviderV4>
  );
};

export default OutlierTaskHeader;
